import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, Upload, Trash2, Play, Music2, Loader2, X, DoorOpen, Search } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function UploadModal({ guildId, onClose, onUploaded }) {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFile = (f) => {
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file || !name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('file', file);
      await axios.post(`${API}/api/soundboard/${guildId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-2xl p-6 w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-semibold font-[Outfit] text-lg">Upload Sound</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div
          className="border-2 border-dashed border-[var(--border-md)] hover:border-[#5865F2]/50 rounded-xl p-8 text-center cursor-pointer transition-colors mb-4"
          onClick={() => fileRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
        >
          <input ref={fileRef} type="file" accept=".mp3,.wav,.ogg,.m4a,.flac" className="hidden" onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
          {file ? (
            <div>
              <Music2 size={28} className="text-[#5865F2] mx-auto mb-2" />
              <p className="text-white text-sm font-medium">{file.name}</p>
              <p className="text-zinc-500 text-xs mt-0.5">{formatSize(file.size)}</p>
            </div>
          ) : (
            <div>
              <Upload size={28} className="text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">Drop an audio file or click to browse</p>
              <p className="text-zinc-600 text-xs mt-1">MP3, WAV, OGG, M4A, FLAC · Max 5MB</p>
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="text-xs text-zinc-500 font-[Manrope] uppercase tracking-widest block mb-1.5">Display name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Air Horn"
            maxLength={64}
            className="w-full bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-xl px-3 py-2.5 text-white text-sm font-[Manrope] placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50"
          />
        </div>

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-[var(--border-md)] text-zinc-400 hover:text-white text-sm font-[Manrope] transition-colors">Cancel</button>
          <button
            onClick={submit}
            disabled={!file || !name.trim() || loading}
            className="flex-1 py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            Upload
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function SoundCard({ sound, guildId, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const play = async () => {
    setPlaying(true);
    setFeedback(null);
    try {
      await axios.post(`${API}/api/soundboard/${guildId}/${sound.sound_id}/play`);
      setFeedback({ ok: true, msg: 'Playing!' });
    } catch (e) {
      setFeedback({ ok: false, msg: e.response?.data?.detail || 'Failed' });
    } finally {
      setPlaying(false);
      setTimeout(() => setFeedback(null), 2500);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/soundboard/${guildId}/${sound.sound_id}`);
      onDelete(sound.sound_id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
      className="bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl p-4 flex flex-col gap-3 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#5865F2]/20 flex items-center justify-center flex-shrink-0">
            <Volume2 size={18} className="text-[#5865F2]" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium text-sm font-[Manrope] truncate">{sound.name}</p>
            <p className="text-zinc-600 text-xs">{formatSize(sound.size)} · {sound.ext.replace('.', '').toUpperCase()}</p>
          </div>
        </div>
        <button onClick={remove} disabled={deleting}
          className="text-zinc-700 hover:text-red-400 transition-colors flex-shrink-0 p-1">
          {deleting ? <Loader2 size={14} className="animate-spin text-red-400" /> : <Trash2 size={14} />}
        </button>
      </div>

      <button
        onClick={play}
        disabled={playing}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-[#5865F2]/15 hover:bg-[#5865F2]/30 text-[#5865F2] text-sm font-medium font-[Manrope] transition-colors disabled:opacity-50"
      >
        {playing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        Play in Voice
      </button>

      <AnimatePresence>
        {feedback && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className={`text-xs text-center font-[Manrope] ${feedback.ok ? 'text-emerald-400' : 'text-red-400'}`}>
            {feedback.msg}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function JoinSoundsSection({ guildId, sounds }) {
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [member, setMember] = useState(null);
  const [soundId, setSoundId] = useState('');
  const [stay, setStay] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/join-sounds/${id}`);
      setEntries(data.entries || []);
    } catch {}
  };

  useEffect(() => { if (guildId) load(guildId); }, [guildId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!query.trim() || member) { setResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/moderation/members/${guildId}`, { params: { q: query.trim() } })
        .then(r => setResults(r.data.members || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, member, guildId]);

  const save = async (entry) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/join-sounds/${guildId}`, entry);
      await load(guildId);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save entrance sound');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!member || !soundId) return;
    if (await save({ user_id: member.id, sound_id: soundId, stay, enabled: true })) {
      toast.success(`Entrance sound set for ${member.display_name}`);
      setMember(null); setQuery(''); setSoundId(''); setStay(true);
    }
  };

  const remove = async (userId) => {
    try {
      await axios.delete(`${API}/api/join-sounds/${guildId}/${userId}`);
      setEntries(prev => prev.filter(e => e.user_id !== userId));
    } catch { toast.error('Failed to remove'); }
  };

  const inputCls = 'w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#5865F2]/50';

  return (
    <section className="mt-10">
      <div className="flex items-center gap-2 mb-1">
        <DoorOpen size={18} className="text-[#5865F2]" />
        <h2 className="text-lg font-semibold text-[var(--text-primary)] font-[Outfit]">Entrance Sounds</h2>
      </div>
      <p className="text-[var(--text-muted)] text-sm font-[Manrope] mb-4">
        When one of these members joins a voice channel, the bot joins them and plays their sound. It never interrupts music. Also available with <code className="text-[#5865F2] bg-[#5865F2]/10 px-1 rounded">/joinsound</code>.
      </p>

      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 mb-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] items-end">
          <div className="relative">
            <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Member</label>
            {member ? (
              <button onClick={() => { setMember(null); setQuery(''); }}
                className={`${inputCls} flex items-center gap-2 text-left`} title="Change member">
                {member.avatar_url && <img src={member.avatar_url} alt="" className="w-5 h-5 rounded-full" />}
                <span className="truncate">{member.display_name}</span>
                <X size={14} className="ml-auto text-[var(--text-muted)]" />
              </button>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search members…" className={`${inputCls} pl-8`} />
              </div>
            )}
            {results.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-xl shadow-xl max-h-60 overflow-y-auto">
                {results.map(m => (
                  <button key={m.id} onClick={() => { setMember(m); setResults([]); }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-hover)] text-left">
                    {m.avatar_url && <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full" />}
                    <span className="text-sm text-[var(--text-primary)] font-[Manrope] truncate">{m.display_name}</span>
                    <span className="text-xs text-[var(--text-muted)] truncate">{m.username}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5">Sound</label>
            <select value={soundId} onChange={e => setSoundId(e.target.value)} className={inputCls}>
              <option value="">Choose a sound…</option>
              {sounds.map(s => <option key={s.sound_id} value={s.sound_id}>{s.name}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] font-[Manrope] pb-2.5 cursor-pointer" title="Stay in the channel and follow them afterwards">
            <input type="checkbox" checked={stay} onChange={e => setStay(e.target.checked)} className="accent-[#5865F2]" />
            Stay with them
          </label>
          <button onClick={add} disabled={!member || !soundId || saving}
            className="px-4 py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40">
            Add
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="text-[var(--text-muted)] text-sm font-[Manrope]">No entrance sounds yet.</p>
      ) : (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl divide-y divide-[var(--border)]">
          {entries.map(e => (
            <div key={e.user_id} className="flex items-center gap-3 px-4 py-3">
              {e.avatar_url
                ? <img src={e.avatar_url} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                : <div className="w-8 h-8 rounded-full bg-[#5865F2]/20 flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[var(--text-primary)] font-medium font-[Manrope] truncate">{e.display_name || e.user_id}</p>
                <p className="text-xs text-[var(--text-muted)] font-[Manrope] truncate">
                  🔊 {e.sound_name || 'Missing sound'}
                </p>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] font-[Manrope] cursor-pointer">
                <input type="checkbox" checked={e.stay} disabled={saving}
                  onChange={ev => save({ user_id: e.user_id, sound_id: e.sound_id, stay: ev.target.checked, enabled: e.enabled })}
                  className="accent-[#5865F2]" />
                Stay
              </label>
              <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] font-[Manrope] cursor-pointer">
                <input type="checkbox" checked={e.enabled} disabled={saving}
                  onChange={ev => save({ user_id: e.user_id, sound_id: e.sound_id, stay: e.stay, enabled: ev.target.checked })}
                  className="accent-[#5865F2]" />
                On
              </label>
              <button onClick={() => remove(e.user_id)} className="text-[var(--text-muted)] hover:text-red-400 p-1" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Soundboard() {
  const { selectedGuild } = useGuild();
  const guildId = selectedGuild?.id;
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const load = async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/soundboard/${id}`);
      setSounds(data.sounds || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(guildId); }, [guildId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = (sound_id) => setSounds(prev => prev.filter(s => s.sound_id !== sound_id));

  return (
    <div className="min-h-screen bg-[var(--bg-base)] lg:pl-60">
      <Sidebar />
      <main className="px-4 sm:px-6 lg:px-8 py-8 pt-20 lg:pt-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white font-[Outfit]">Soundboard</h1>
            <p className="text-zinc-500 text-sm font-[Manrope] mt-0.5">Upload audio clips and play them in your voice channel</p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors"
          >
            <Upload size={15} />
            Upload Sound
          </button>
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 mb-6 text-sm text-zinc-500 font-[Manrope]">
          The bot must be in a voice channel to play sounds. Use <code className="text-[#5865F2] bg-[#5865F2]/10 px-1 rounded">/join</code> in Discord first, or start playing music from the dashboard.
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-[var(--bg-hover)] rounded-xl animate-pulse" />)}
          </div>
        ) : sounds.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-[#5865F2]/10 flex items-center justify-center mx-auto mb-4">
              <Volume2 size={28} className="text-[#5865F2]" />
            </div>
            <p className="text-zinc-300 font-semibold font-[Outfit] mb-1">No sounds yet</p>
            <p className="text-zinc-600 text-sm font-[Manrope]">Upload your first audio clip to get started</p>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <AnimatePresence>
              {sounds.map(s => (
                <SoundCard key={s.sound_id} sound={s} guildId={guildId} onDelete={handleDelete} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {selectedGuild?.is_staff && !selectedGuild?.is_global && sounds.length > 0 && (
          <JoinSoundsSection guildId={guildId} sounds={sounds} />
        )}
      </main>

      <AnimatePresence>
        {showUpload && <UploadModal guildId={guildId} onClose={() => setShowUpload(false)} onUploaded={() => load(guildId)} />}
      </AnimatePresence>
    </div>
  );
}
